import React, { useCallback, useEffect, useState } from "react";
import Select from 'react-select'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDrawPolygon, faPenLine } from "@fortawesome/pro-light-svg-icons";
import ColorBox from 'devextreme-react/color-box';
import { Trans, useTranslation } from "react-i18next";

import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context";

import MeasureShape from "@/pages/takeoff/context/project-measure/models/measure-shape.ts";
import MeasureRepresentation from "@/pages/takeoff/context/project-measure/models/measure-representation.ts";
import Toggle from "@/components/toggle/index.tsx";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import ToggleButton from "@/components/buttons/button-toggle";

import addMeasureTypeAction from "./add-measure-type-action";
import LoadingButton from "@/components/buttons/loading-button";
import { BaseDialogProps } from "@/components/dialogs/base-dialog";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";

interface AddMeasureTypeFormElements extends HTMLFormControlsCollection {
    name: HTMLInputElement;
}

interface FormElements extends HTMLFormElement { readonly elements: AddMeasureTypeFormElements }

interface AddMeasureTypeDialogProps extends BaseDialogProps {
    measureTypeAddedCallback: (type: MeasureType) => void;
}

const AddMeasureTypeDialog: React.FC<AddMeasureTypeDialogProps> = ({ open, onClose, measureTypeAddedCallback }) => {
    const { t } = useTranslation();
    const [isPending, setIsPending] = useState(false);

    const { predefinedShapes, projectMeasureTypes, addMeasureType } = useProjectMeasureContext();

    const getPredefinedShape = useCallback((name: string): MeasureShape => {
        const shape = predefinedShapes.find(s => s.name === name);

        if (!shape) {
            throw new Error(`Shape prédéfinie introuvable : ${name}`);
        }

        return shape;
    }, [predefinedShapes]);

    const [selectedShape, setSelectedShape] = useState<MeasureShape>(getPredefinedShape('Line'));
    const [hasAdditionalConstruction, setHasAdditionalConstruction] = useState(false);
    const [selectedMeasureRepresentation, setSelectedMeasureRepresentation] = useState<MeasureRepresentation | undefined>(undefined);

    const [error, setError] = useState<string | undefined>(undefined);

    const resetForm = useCallback(() => {
        setSelectedMeasureRepresentation(undefined);
        setHasAdditionalConstruction(false);
        setError(undefined);
    }, []);

    const closeDialog = useCallback(() => {
        onClose(false);
        resetForm();
    }, [resetForm, onClose]);

    const handleSubmit = useCallback(async (event: React.FormEvent<FormElements>) => {
        event.preventDefault();
        event.stopPropagation();

        const formData = new FormData(event.currentTarget);

        setIsPending(true);
        setError(undefined);

        const result = await addMeasureTypeAction({
            formData,
            predefinedShapes,
            projectMeasureTypes,
            addMeasureType
        });

        setIsPending(false);
        if (result.success && result.addedMeasureType) {
            measureTypeAddedCallback(result.addedMeasureType);
            closeDialog();
        } else if (result.message) {
            setError(result.message);
        }
    }, [addMeasureType, closeDialog, measureTypeAddedCallback, predefinedShapes, projectMeasureTypes])

    useEffect(() => {
        if (!hasAdditionalConstruction) return;
        setSelectedMeasureRepresentation(selectedShape.representations[0]);
    }, [hasAdditionalConstruction, selectedShape]);

    const getInputType = (type: unknown): React.HTMLInputTypeAttribute => {
        if (typeof type !== "string") {
            return "text";
        }

        const allowedTypes: React.HTMLInputTypeAttribute[] = ["text", "number"];

        return allowedTypes.includes(type as React.HTMLInputTypeAttribute)
            ? (type as React.HTMLInputTypeAttribute)
            : "text";
    };

    return (
        <ClosableDialog open={open} onClose={closeDialog} title='Nouveau type de métré'>
            <form onSubmit={handleSubmit} className='flex flex-col gap-y-2'>
                {error && <div className='bg-red-400 rounded-sm p-2 text-white col-span-2'>{error}</div>}

                <div className={`grid grid-cols-[4.5rem_1fr] items-center gap-y-4 gap-x-4 ${hasAdditionalConstruction && 'border-b pb-2'}`}>
                    <div className='font-semibold'>Nom</div>
                    <input name="name" type="text" tabIndex={0} autoFocus={true}
                        className="rounded-md px-2 py-0.75 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600" />

                    <div className='font-semibold'>Forme</div>
                    <input type="hidden" name="shape" value={selectedShape?.name} />
                    <div className='grid grid-cols-4 gap-x-2'>
                        <ToggleButton variant='ghost' toggled={selectedShape?.name === 'Line'} setIsToggled={() => {
                            setSelectedShape(getPredefinedShape('Line'))
                        }}>
                            <FontAwesomeIcon icon={faPenLine} className='text-xl mx-auto' />
                        </ToggleButton>

                        {/* <ToggleButton variant='ghost' toggled={selectedShape?.name === 'Polyline'} setIsToggled={() => { */}
                        {/*    setSelectedShape(getPredefinedShape('Polyline')) */}
                        {/* }}> */}
                        {/*    <FontAwesomeIcon icon={faScribble} className='text-xl mx-auto'/> */}
                        {/* </ToggleButton> */}

                        <ToggleButton variant='ghost' toggled={selectedShape?.name === 'Surface'} setIsToggled={() => {
                            setSelectedShape(getPredefinedShape('Surface'))
                        }}>
                            <FontAwesomeIcon icon={faDrawPolygon} className='text-xl mx-auto' />
                        </ToggleButton>

                        {/* <ToggleButton variant='ghost' toggled={selectedShape?.name === 'Point'} setIsToggled={() => { */}
                        {/*    setSelectedShape(getPredefinedShape('Point')) */}
                        {/* }}> */}
                        {/*    <FontAwesomeIcon icon={faLocationDot} className='text-xl mx-auto'/> */}
                        {/* </ToggleButton> */}
                    </div>

                    <div>Couleur</div>
                    <ColorBox name='color'
                        defaultValue='#3B82F6FF'
                        editAlphaChannel={true} applyButtonText='Appliquer' cancelButtonText='Annuler' height={30}
                        showMaskMode='onFocus' openOnFieldClick={true} />

                    <input type='hidden' name='hasAdditionalConstruction' value={hasAdditionalConstruction ? 'true' : 'false'} />
                    <Toggle toggled={hasAdditionalConstruction}
                        onToggledChange={(enabled) => setHasAdditionalConstruction(enabled)} />

                    <input type='hidden' name='measureRepresentationId' value={selectedMeasureRepresentation?.measureRepresentationId ?? ''} />
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div>Construction d'objets</div>
                        {hasAdditionalConstruction && (
                            <Select getOptionLabel={option => t(option.name)}
                                getOptionValue={option => option.measureRepresentationId}
                                options={selectedShape?.representations}
                                placeholder={'Sélectionner'}
                                value={selectedMeasureRepresentation} isSearchable={false}
                                classNames={{
                                    container: () => "flex-1",
                                    control: () => "!min-h-0 !rounded-md !border-gray-200",
                                    menu: () => "text-sm",
                                    menuList: () => "text-sm",
                                    singleValue: () => "text-sm",
                                    placeholder: () => "text-sm",
                                    indicatorsContainer: () => "-my-1",
                                }}
                                onChange={selected => {
                                    if (selected != null) setSelectedMeasureRepresentation(selected)
                                }}>
                            </Select>
                        )}
                    </div>
                </div>

                {hasAdditionalConstruction && (
                    <div className='grid grid-cols-[auto_1fr] gap-4 ml-22.5 mt-1 items-center'>
                        {selectedMeasureRepresentation?.dataFormatType &&
                            Object.entries(selectedMeasureRepresentation.dataFormatType)
                                .filter(([key]) => key !== "color" && key !== "alpha")
                                .map(([key, type]) => (
                                    <React.Fragment key={key}>
                                        <span>
                                            <Trans>{key}</Trans> (<Trans>{key}_unit</Trans>)
                                        </span>

                                        <input name={`dataFormatType_${key}`} type={getInputType(type)}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600 sm:text-sm/6"
                                        />
                                    </React.Fragment>
                                ))
                        }
                    </div>
                )}

                <LoadingButton className='ml-auto' type='submit' isLoading={isPending} body='Ajouter' />
            </form>
        </ClosableDialog>
    )
};

export default AddMeasureTypeDialog;